import React, { useEffect, useMemo } from 'react'
import { Alert, Button, Flex, Typography } from 'antd'
import { useUnit } from 'effector-react'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import {
  $wallets,
  $walletsHasError,
  $walletsTotal,
  getWalletsFx,
  walletsRequested,
} from '../model'
import { WalletGuardWalletsRequest } from '../types'
import {
  $hasError,
  $paymentSystems,
  $stats,
  dashboardRequested,
  getDashboardFx,
  paymentSystemsRequested,
  walletCheckRequested,
} from './model'
import { WGDashboardStatistics } from './WGDashboardStatistics'
import { WGDashboardWalletCheckModal } from './WGDashboardWalletCheckModal'
import { WGDashboardWallets } from './WGDashboardWallets'

const FILTER_CONFIG = {
  currency: { type: 'array' as const },
  type: { type: 'array' as const },
  paymentSystems: { type: 'array' as const },
  status: { type: 'array' as const },
}

export const WalletGuardDashboardPage = () => {
  const {
    stats,
    wallets,
    walletsTotal,
    statsLoading,
    walletsLoading,
    hasError,
    walletsHasError,
    paymentSystems,
    requestDashboard,
    requestPaymentSystems,
    requestWallets,
    checkWallet,
  } = useUnit({
    stats: $stats,
    wallets: $wallets,
    walletsTotal: $walletsTotal,
    statsLoading: getDashboardFx.pending,
    walletsLoading: getWalletsFx.pending,
    hasError: $hasError,
    walletsHasError: $walletsHasError,
    paymentSystems: $paymentSystems,
    requestDashboard: dashboardRequested,
    requestPaymentSystems: paymentSystemsRequested,
    requestWallets: walletsRequested,
    checkWallet: walletCheckRequested,
  })

  const { filters, setFilter, resetFilters, hasActiveFilters } =
    useUrlFilters(FILTER_CONFIG)

  // Дашборд показывает все кошельки сразу, без пагинации.
  const walletsRequest = useMemo<WalletGuardWalletsRequest>(
    () => ({ limit: 100, page: 1, filters }),
    [filters],
  )

  useEffect(() => {
    requestDashboard()
    requestPaymentSystems()
  }, [requestDashboard, requestPaymentSystems])

  useEffect(() => {
    requestWallets(walletsRequest)
  }, [requestWallets, walletsRequest])

  const lastUpdatedAt = stats?.lastUpdatedAt
    ? new Date(stats.lastUpdatedAt)
    : null

  return (
    <Flex vertical gap={24}>
      <Flex vertical gap={4}>
        <Typography.Title level={1} style={{ margin: 0 }}>
          Дашборд
        </Typography.Title>
        {lastUpdatedAt && !Number.isNaN(lastUpdatedAt.getTime()) && (
          <Typography.Text type="secondary">
            Обновлено: {lastUpdatedAt.toLocaleString('ru-RU')}
          </Typography.Text>
        )}
      </Flex>

      {hasError ? (
        <Alert
          type="error"
          showIcon
          message="Не удалось загрузить статистику кошельков"
          action={<Button onClick={() => requestDashboard()}>Повторить</Button>}
        />
      ) : (
        <WGDashboardStatistics stats={stats} loading={statsLoading || !stats} />
      )}

      {walletsHasError ? (
        <Alert
          type="error"
          showIcon
          message="Не удалось загрузить данные кошельков"
          action={
            <Button onClick={() => requestWallets(walletsRequest)}>
              Повторить
            </Button>
          }
        />
      ) : (
        <WGDashboardWallets
          wallets={wallets ?? []}
          total={walletsTotal}
          loading={walletsLoading || wallets === null}
          filters={filters}
          hasActiveFilters={hasActiveFilters}
          paymentSystems={paymentSystems}
          onFilterChange={setFilter}
          onFiltersReset={resetFilters}
          onWalletCheck={checkWallet}
        />
      )}

      <WGDashboardWalletCheckModal />
    </Flex>
  )
}
